import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTutorStore } from '@shared/state';
import type { TutorCheckpointData } from '@shared/types/tutor';
import { Button } from '../ui/Button';

interface TutorCheckpointProps {
  checkpoint: TutorCheckpointData;
  messageId: string;
}

export function TutorCheckpoint({ checkpoint, messageId }: TutorCheckpointProps) {
  const submitCheckpointAnswer = useTutorStore((s) => s.submitCheckpointAnswer);
  const [selected, setSelected] = useState<string | null>(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [loading, setLoading] = useState(false);

  const isMultipleChoice = checkpoint.questionType === 'multiple_choice';
  const isAnswered = checkpoint.userAnswer !== null;
  const score = checkpoint.score;

  async function handleSubmit() {
    const answer = isMultipleChoice ? (selected ?? '') : textAnswer;
    if (!answer) return;
    setLoading(true);
    try {
      await submitCheckpointAnswer(messageId, answer);
    } catch (err) {
      console.error('Checkpoint submit error:', err);
    } finally {
      setLoading(false);
    }
  }

  const scoreColor = score !== null
    ? score >= 80 ? 'text-success' : score >= 50 ? 'text-warning' : 'text-error'
    : '';

  return (
    <div className="mt-3 p-4 rounded-lg bg-dark-surface border border-dark-border">
      <p className="text-xs text-indigo-400 font-body uppercase tracking-wider mb-2 font-semibold">Test Me</p>
      <p className="text-sm text-content font-body mb-3">{checkpoint.question}</p>

      {isMultipleChoice && checkpoint.options && (
        <div className="space-y-1.5 mb-3">
          {checkpoint.options.map((opt, i) => {
            const isSelected = selected === String(i);
            const isCorrect = isAnswered && String(i) === checkpoint.correctAnswer;
            const isWrong = isAnswered && isSelected && !isCorrect;

            return (
              <button
                key={i}
                onClick={() => !isAnswered && setSelected(String(i))}
                disabled={isAnswered}
                className={`w-full text-left px-3 py-2 rounded text-xs font-body transition-colors cursor-pointer
                  ${isCorrect ? 'bg-success/15 border border-success text-success' : ''}
                  ${isWrong ? 'bg-error/15 border border-error text-error' : ''}
                  ${!isAnswered && isSelected ? 'bg-indigo-500/15 border border-indigo-500 text-content' : ''}
                  ${!isAnswered && !isSelected ? 'bg-dark-subtle border border-dark-border text-content-secondary hover:border-indigo-500/50' : ''}
                  ${isAnswered && !isCorrect && !isWrong ? 'opacity-50 border border-dark-border text-content-dim' : ''}
                `}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}

      {!isMultipleChoice && !isAnswered && (
        <textarea
          value={textAnswer}
          onChange={(e) => setTextAnswer(e.target.value)}
          placeholder="Type your answer..."
          rows={3}
          className="w-full px-3 py-2 rounded bg-dark-subtle border border-dark-border text-xs text-content font-body
            placeholder:text-content-dim focus:outline-none focus:ring-1 focus:ring-indigo-500/50 mb-3 resize-none"
        />
      )}

      {!isAnswered ? (
        <Button
          size="sm"
          onClick={handleSubmit}
          loading={loading}
          disabled={isMultipleChoice ? !selected : !textAnswer.trim()}
        >
          Check Answer
        </Button>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
          {score !== null && (
            <p className={`text-sm font-body font-semibold ${scoreColor}`}>
              {score >= 80 ? 'Great work!' : score >= 50 ? 'Getting there' : 'Let\'s review'}
              <span className="text-xs font-normal text-content-muted ml-1">({score}%)</span>
            </p>
          )}
          {checkpoint.feedback && (
            <p className="text-xs text-content-secondary font-body">{checkpoint.feedback}</p>
          )}
        </motion.div>
      )}
    </div>
  );
}
